import { Icon } from '@iconify/react'
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@nextui-org/react'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useWorkspaceContextData } from 'src/context/workspace'
import api from 'src/lib/api'
import Mixpanel from 'src/lib/mixpanel'
interface IInviteTeamMemberProps {
  isOpen: boolean
  onClose: () => void
  loadMembers: (activeWorkspace: string) => void
}
const permissionLabels: Record<string, string> = {
  VIEWER: 'Can View',
  EDITOR: 'Can Edit',
}
function getFirstItemOfSet(set: Set<string>) {
  for (const item of set) {
    if (item) {
      return item
    }
  }
  return 'VIEWER'
}

export default function InviteTeamMember({ isOpen, onClose, loadMembers }: IInviteTeamMemberProps) {
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(new Set(['EDITOR']))
  const [userEmails, setUserEmails] = useState<string>('')
  const [inviting, setInviting] = useState<boolean>(false)
  const { activeWorkspace } = useWorkspaceContextData()
  const inviteUsers = async () => {
    setInviting(true)
    await api.post(`/workspaces/${activeWorkspace}/invite`, {
      emails: userEmails.split(','),
      role: getFirstItemOfSet(selectedKeys).toString(),
    })
    loadMembers(activeWorkspace)
    setInviting(false)
    setUserEmails('')
    onClose()
    toast.success('User has been invited')
  }
  return (
    <Modal
      className="no-scrollbar"
      backdrop="blur"
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="5xl"
      isDismissable={false}
    >
      <div
        onClick={(e) => {
          if (e.target.getAttribute('data-slot') === 'wrapper') {
            onClose()
          }
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1"></ModalHeader>
          <ModalBody>
            <div className="flex flex-col grow gap-7">
              <div className="flex flex-col items-center">
                <h4 className="text-large">Invite team members</h4>
                <p className="text-center text-small text-default-500">Invite new members to your organization.</p>
              </div>
              <div className="flex flex-col gap-3 items-center">
                <Input
                  onChange={(e) => setUserEmails(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      // Track button click for inviting team members
                      Mixpanel.track('Invite member clicked', {
                        user_id: localStorage.getItem('userId'), // Example, replace with actual user ID retrieval logic
                        workspace_id: activeWorkspace, // Active workspace
                        email_count: userEmails.split(',').length, // Tracks number of emails being invited
                      })
                      inviteUsers()
                    }
                  }}
                  endContent={
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          className="text-default-500"
                          endContent={
                            <span className="hidden sm:flex">
                              <Icon icon="solar:alt-arrow-down-linear" />
                            </span>
                          }
                          size="sm"
                          variant="light"
                        >
                          {Array.from(selectedKeys)
                            .map((key) => permissionLabels[key])
                            .join(', ')}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        selectedKeys={selectedKeys}
                        selectionMode="single"
                        onSelectionChange={setSelectedKeys}
                      >
                        <DropdownItem key="EDITOR">Can edit</DropdownItem>
                        <DropdownItem key="VIEWER">Can view</DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  }
                  label="Email address"
                  labelPlacement="outside"
                  value={userEmails}
                  placeholder="Separate multiple emails with commas"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onClose}>
              Cancel
            </Button>
            <Button color="secondary" onPress={inviteUsers} isDisabled={userEmails.length === 0 || inviting}>
              {inviting && <Spinner size={'sm'} />}
              {' Invite'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </div>
    </Modal>
  )
}
